"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.r2rClient = void 0;
const axios_1 = __importDefault(require("axios"));
const baseClient_1 = require("./baseClient");
const chunks_1 = require("./v3/clients/chunks");
const collections_1 = require("./v3/clients/collections");
const conversations_1 = require("./v3/clients/conversations");
const documents_1 = require("./v3/clients/documents");
const graphs_1 = require("./v3/clients/graphs");
const indices_1 = require("./v3/clients/indices");
const prompts_1 = require("./v3/clients/prompts");
const retrieval_1 = require("./v3/clients/retrieval");
const system_1 = require("./v3/clients/system");
const users_1 = require("./v3/clients/users");
let fs;
if (typeof window === "undefined") {
    fs = require("fs");
}
class r2rClient extends baseClient_1.BaseClient {
    constructor(baseURL, anonymousTelemetry = true, options = {}) {
        super(baseURL, "", anonymousTelemetry, options.enableAutoRefresh);
        this.chunks = new chunks_1.ChunksClient(this);
        this.collections = new collections_1.CollectionsClient(this);
        this.conversations = new conversations_1.ConversationsClient(this);
        this.documents = new documents_1.DocumentsClient(this);
        this.graphs = new graphs_1.GraphsClient(this);
        this.indices = new indices_1.IndiciesClient(this);
        this.prompts = new prompts_1.PromptsClient(this);
        this.retrieval = new retrieval_1.RetrievalClient(this);
        this.system = new system_1.SystemClient(this);
        this.users = new users_1.UsersClient(this);
        this.axiosInstance = axios_1.default.create({
            baseURL: this.baseUrl,
            headers: {
                "Content-Type": "application/json",
            },
        });
        this.getTokensCallback = options.getTokensCallback;
        this.setTokensCallback = options.setTokensCallback;
        this.onRefreshFailedCallback = options.onRefreshFailedCallback;
        // 1) Request interceptor: attach current access token (if any)
        this.axiosInstance.interceptors.request.use((config) => {
            var _a;
            const tokenData = (_a = this.getTokensCallback) === null || _a === void 0 ? void 0 : _a.call(this);
            const accessToken = (tokenData === null || tokenData === void 0 ? void 0 : tokenData.accessToken) || null;
            if (accessToken) {
                config.headers["Authorization"] = `Bearer ${accessToken}`;
            }
            return config;
        }, (error) => {
            console.error("[r2rClient] Request interceptor error:", error);
            return Promise.reject(error);
        });
        // 2) Response interceptor: see if we got 401/403 => attempt to refresh
        this.setupResponseInterceptor();
    }
    setupResponseInterceptor() {
        this.axiosInstance.interceptors.response.use((response) => response, async (error) => {
            var _a, _b, _c, _d, _e, _f;
            const status = (_a = error.response) === null || _a === void 0 ? void 0 : _a.status;
            const failingUrl = (_b = error.config) === null || _b === void 0 ? void 0 : _b.url;
            const errorData = (_c = error.response) === null || _c === void 0 ? void 0 : _c.data;
            // 1) If the refresh endpoint itself fails => don't try again
            if (failingUrl === null || failingUrl === void 0 ? void 0 : failingUrl.includes("/v3/users/refresh-token")) {
                console.error("[r2rClient] Refresh call itself returned 401/403 => logging out");
                (_d = this.onRefreshFailedCallback) === null || _d === void 0 ? void 0 : _d.call(this);
                return Promise.reject(error);
            }
            // 2) If normal request => attempt refresh IF it's really an invalid/expired token
            // We'll check either an explicit "error_code" or text in "message"
            // Adjust to match your server's structure!
            const isTokenError = !!(errorData === null || errorData === void 0 ? void 0 : errorData.error_code) &&
                errorData.error_code.toUpperCase() === "TOKEN_EXPIRED";
            // Or fallback to matching common phrases if no error_code is set:
            const msg = ((errorData === null || errorData === void 0 ? void 0 : errorData.message) || "").toLowerCase();
            const looksLikeTokenIssue = msg.includes("invalid token") ||
                msg.includes("token expired") ||
                msg.includes("credentials");
            // If either of those checks is true, we consider it an auth token error:
            const isAuthError = isTokenError || looksLikeTokenIssue;
            if ((status === 401 || status === 403) &&
                this.getTokensCallback &&
                isAuthError) {
                // Check if we have a refresh token
                const { refreshToken } = this.getTokensCallback();
                if (!refreshToken) {
                    console.error("[r2rClient] No refresh token found => logout");
                    (_e = this.onRefreshFailedCallback) === null || _e === void 0 ? void 0 : _e.call(this);
                    return Promise.reject(error);
                }
                // Attempt refresh
                try {
                    const refreshResponse = await this.users.refreshAccessToken();
                    const newAccessToken = refreshResponse.results.accessToken.token;
                    const newRefreshToken = refreshResponse.results.refreshToken.token;
                    // set new tokens
                    this.setTokens(newAccessToken, newRefreshToken);
                    // Re-try the original request
                    if (error.config) {
                        error.config.headers["Authorization"] =
                            `Bearer ${newAccessToken}`;
                        return this.axiosInstance.request(error.config);
                    }
                    else {
                        console.warn("[r2rClient] No request config found to retry. Possibly manual re-fetch needed");
                    }
                }
                catch (refreshError) {
                    console.error("[r2rClient] Refresh attempt failed => logging out. Error was:", refreshError);
                    (_f = this.onRefreshFailedCallback) === null || _f === void 0 ? void 0 : _f.call(this);
                    return Promise.reject(refreshError);
                }
            }
            // 3) If not a 401/403 or it's a 401/403 that isn't token-related => just reject
            return Promise.reject(error);
        });
    }
    makeRequest(method, endpoint, options = {}) {
        return this._makeRequest(method, endpoint, options, "v3");
    }
    getRefreshToken() {
        return this.refreshToken;
    }
    setTokens(accessToken, refreshToken) {
        var _a;
        super.setTokens(accessToken || "", refreshToken || "");
        (_a = this.setTokensCallback) === null || _a === void 0 ? void 0 : _a.call(this, accessToken, refreshToken);
    }
}
exports.r2rClient = r2rClient;
exports.default = r2rClient;
//# sourceMappingURL=r2rClient.js.map